import { createFileRoute, Link } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { sharedPlaygroundApi } from '@/lib/api'
import {
  ReactFlow,
  Controls,
  Background,
  MiniMap,
  ReactFlowProvider,
  type EdgeTypes,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import '@/styles/playground.css'
import { Box, Button, Flex, Heading, HStack, Spinner, Text, VStack } from '@chakra-ui/react'

// Import all node types (same as playground)
import GenericNode from '@/components/playground/GenericNode'
import DynamicNode from '@/components/playground/DynamicNode'
import ButtonEdge from '@/components/playground/ButtonEdge'

// Flow template node components
import EdgeNode from '~/components/nodes/edgeNode'
import EdgeNodeBi from '~/components/nodes/edgeNodeBi'
import CloudNode from '~/components/nodes/cloudNode'
import CloudNodeBi from '~/components/nodes/cloudNodeBi'
import EntBrokerNode from '~/components/nodes/entBrokerNode'
import EntBrokerNodeBi from '~/components/nodes/entBrokerNodeBi'
import DittoNode from '~/components/nodes/dittoNode'
import DittoNodeBi from '~/components/nodes/dittoNodeBi'
import MobileNode from '~/components/nodes/mobileNode'
import WebNode from '~/components/nodes/webNode'
import MachineLearningNode from '~/components/nodes/machineLearningNode'
import MachineLearningTopNode from '~/components/nodes/machineLearningTopNode'
import MicroServiceNode from '~/components/nodes/microServiceNode'
import PostgresNode from '~/components/nodes/postgresNode'
import PostgresNodeML from '~/components/nodes/postgresNodeML'
import PostgresTopNode from '~/components/nodes/postgresTopNode'
import DatalakeNode from '~/components/nodes/datalakeNode'
import StreamingNode from '~/components/nodes/streamingNode'
import ConfluentNode from '~/components/nodes/ConfluentNode'
import MongoDBNode from '~/components/nodes/mongoDBNode'
import PLCNode from '~/components/nodes/plcNode'
import PLCNodeSiemens from '~/components/nodes/plcNodeSiemens'
import PLCNodeRockwell from '~/components/nodes/plcNodeRockwell'
import PLCNodeWago from '~/components/nodes/plcNodeWago'
import PLCNodePhoenixContact from '~/components/nodes/plcNodePhoenixContact'
import ArmNode from '~/components/nodes/armNode'
import ArmSimpleNode from '~/components/nodes/armSimpleNode'
import DrillingNode from '~/components/nodes/drillingNode'
import PaintNode from '~/components/nodes/paintNode'
import StampingNode from '~/components/nodes/stampingNode'
import ScadaTopNode from '~/components/nodes/scadaTopNode'
import HmiBottomNode from '~/components/nodes/hmiBottomNode'
import MesBottomNode from '~/components/nodes/mesBottomNode'
import ReportingNode from '~/components/nodes/reportingNode'
import HistorianNode from '~/components/nodes/historianNode'
import ErpNode from '~/components/nodes/erpNode'
import TiresNode from '~/components/nodes/tiresNode'
import CollisionNode from '~/components/nodes/collisionNode'
import MotorNode from '~/components/nodes/motorNode'
import TankNode from '~/components/nodes/tankNode'
import CarComputerNode from '~/components/nodes/carComputerNode'
import DriverPadNode from '~/components/nodes/driverPadNode'
import WarehouseNode from '~/components/nodes/warehouseNode'
import StorageNode from '~/components/nodes/StorageNode'
import ConveyorNode from '~/components/nodes/conveyorNode'
import WindTurbineNode from '~/components/nodes/windTubineNode'
import SolarPanelNode from '~/components/nodes/solarPanelNode'
import EvChargerNode from '~/components/nodes/evChargerNode'
import BatteriesNode from '~/components/nodes/batteriesNode'
import ControlSystemNode from '~/components/nodes/controlSystemNode'
import PowerMeterNode from '~/components/nodes/powerMeterNode copy'
import PowerRegulatorNode from '~/components/nodes/powerRegulatorNode'
import OcppGatewayNode from '~/components/nodes/ocppGatewayNode'
import PipelineNode from '~/components/nodes/pipelineNode'
import RefineryNode from '~/components/nodes/refineryNode'
import OilWellNode from '~/components/nodes/oilWellNode'
import RetailFuelNode from '~/components/nodes/retailFuelNode'
import FeederNode from '~/components/nodes/feederNode'
import HopperNode from '~/components/nodes/hopperNode'
import StoragePharmaNode from '~/components/nodes/StoragePharmaNode'
import BioReactorPharmaNode from '~/components/nodes/BioReactorPharmaNode'
import ShippingNode from '~/components/nodes/shippingNode'
import HeatPumpNode from '~/components/nodes/heatpumpNode'
import HeaterNode from '~/components/nodes/heaterNode'
import BoilerNode from '~/components/nodes/boilerNode'
import VentilationNode from '~/components/nodes/ventilationNode'
import EnvSensorNode from '~/components/nodes/envSensorNode'
import EdgeBoxNode from '~/components/nodes/edgeBoxNode'

export const Route = createFileRoute('/s/$shortCode')({
  component: SharedPlaygroundPage,
})

const nodeTypes = {
  genericNode: GenericNode,
  dynamicNode: DynamicNode,
  edgeNode: EdgeNode,
  edgeNodeBi: EdgeNodeBi,
  cloudNode: CloudNode,
  cloudNodeBi: CloudNodeBi,
  entBrokerNode: EntBrokerNode,
  entBrokerNodeBi: EntBrokerNodeBi,
  dittoNode: DittoNode,
  dittoNodeBi: DittoNodeBi,
  mobileNode: MobileNode,
  webNode: WebNode,
  machineLearningNode: MachineLearningNode,
  machineLearningTopNode: MachineLearningTopNode,
  microServiceNode: MicroServiceNode,
  postgresNode: PostgresNode,
  postgresNodeML: PostgresNodeML,
  postgresTopNode: PostgresTopNode,
  datalakeNode: DatalakeNode,
  streamingNode: StreamingNode,
  confluentNode: ConfluentNode,
  mongoDBNode: MongoDBNode,
  reportingNode: ReportingNode,
  historianNode: HistorianNode,
  erpNode: ErpNode,
  scadaTopNode: ScadaTopNode,
  hmiBottomNode: HmiBottomNode,
  mesBottomNode: MesBottomNode,
  plcNode: PLCNode,
  plcNodeSiemens: PLCNodeSiemens,
  plcNodeRockwell: PLCNodeRockwell,
  plcNodeWago: PLCNodeWago,
  plcNodePhoenixContact: PLCNodePhoenixContact,
  armNode: ArmNode,
  armSimpleNode: ArmSimpleNode,
  drillingNode: DrillingNode,
  paintNode: PaintNode,
  stampingNode: StampingNode,
  tiresNode: TiresNode,
  collisionNode: CollisionNode,
  motorNode: MotorNode,
  tankNode: TankNode,
  carComputerNode: CarComputerNode,
  driverPadNode: DriverPadNode,
  warehouseNode: WarehouseNode,
  storageNode: StorageNode,
  conveyorsNode: ConveyorNode,
  windTurbineNode: WindTurbineNode,
  solarPanelNode: SolarPanelNode,
  evChargerNode: EvChargerNode,
  batteriesNode: BatteriesNode,
  controlSystemNode: ControlSystemNode,
  powerMeterNode: PowerMeterNode,
  powerRegulatorNode: PowerRegulatorNode,
  ocppGatewayNode: OcppGatewayNode,
  pipelineNode: PipelineNode,
  refineryNode: RefineryNode,
  oilwellNode: OilWellNode,
  retailfuelNode: RetailFuelNode,
  feederNode: FeederNode,
  hopperNode: HopperNode,
  storagePharmaNode: StoragePharmaNode,
  bioReactorPharmaNode: BioReactorPharmaNode,
  shippingNode: ShippingNode,
  heatPumpNode: HeatPumpNode,
  heaterNode: HeaterNode,
  boilerNode: BoilerNode,
  ventilationNode: VentilationNode,
  envSensorNode: EnvSensorNode,
  edgeBoxNode: EdgeBoxNode,
}

const edgeTypes = {
  button: ButtonEdge,
}

function SharedPlaygroundContent() {
  const { shortCode } = Route.useParams()

  const { data, isLoading, error } = useQuery({
    queryKey: ['shared-playground', shortCode],
    queryFn: () => sharedPlaygroundApi.get(shortCode),
  })

  if (isLoading) {
    return (
      <Flex h="100vh" align="center" justify="center">
        <VStack gap={4}>
          <Spinner size="xl" color="blue.500" />
          <Text color="gray.500">Loading shared playground...</Text>
        </VStack>
      </Flex>
    )
  }

  if (error || !data) {
    return (
      <Flex h="100vh" align="center" justify="center">
        <VStack gap={4} textAlign="center" p={8}>
          <Heading size="xl" color="red.500">
            Playground Not Found
          </Heading>
          <Text color="gray.500" maxW="400px">
            This shared playground link may have expired or been revoked.
          </Text>
          <Link to="/">
            <Button colorPalette="blue">Go to HiveMQ Stories</Button>
          </Link>
        </VStack>
      </Flex>
    )
  }

  return (
    <Box h="100vh" display="flex" flexDirection="column">
      {/* Header */}
      <Box bg="white" borderBottomWidth="1px" px={4} py={3} flexShrink={0}>
        <Flex justify="space-between" align="center">
          <HStack gap={4}>
            {data.customerLogoUrl && (
              <img
                src={data.customerLogoUrl}
                alt="Customer logo"
                style={{ height: 40, objectFit: 'contain' }}
              />
            )}
            <Box>
              <Heading size="md">{data.name}</Heading>
              {data.customerName && (
                <Text fontSize="sm" color="gray.500">
                  {data.customerName}
                </Text>
              )}
            </Box>
          </HStack>
          {data.ownerName && (
            <Text fontSize="sm" color="gray.500">
              Shared by {data.ownerName}
            </Text>
          )}
        </Flex>
      </Box>

      {/* ReactFlow Canvas (read-only) */}
      <Box flex={1}>
        <ReactFlow
          nodes={data.nodes as any}
          edges={data.edges as any}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes as EdgeTypes}
          fitView
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          panOnDrag={true}
          zoomOnScroll={true}
          style={{ backgroundColor: 'var(--chakra-colors-gray-50)' }}
        >
          <Background />
          <Controls position="bottom-right" showInteractive={false} />
          <MiniMap zoomable pannable position="bottom-left" />
        </ReactFlow>
      </Box>

      {/* Footer */}
      <Box bg="gray.50" borderTopWidth="1px" px={4} py={2} flexShrink={0}>
        <Flex justify="space-between" align="center">
          <Text fontSize="xs" color="gray.500">
            Created with HiveMQ Stories 2.0
          </Text>
          <Text fontSize="xs" color="gray.500">
            <a
              href="https://www.hivemq.com"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'inherit', textDecoration: 'underline' }}
            >
              www.hivemq.com
            </a>
          </Text>
        </Flex>
      </Box>
    </Box>
  )
}

function SharedPlaygroundPage() {
  return (
    <ReactFlowProvider>
      <SharedPlaygroundContent />
    </ReactFlowProvider>
  )
}
